// ─────────────────────────────────────────────────────────────────────────────
// format.js — how the Nodes page renders values.
//
// THE RULE: a value the backend did not report renders as an em dash. Never a
// 0, never a blank, never a guess — the user must be able to tell "nothing is
// connected" from "we don't know". 0 and false ARE readings and print as
// themselves; only null/undefined/"" are missing.
//
// Everything that formats a number for this page goes through here. It used to
// be re-implemented per component (three copies of the count formatter alone),
// which is how a rule quietly stops being one rule.
//
// These live in the page, not in services/nodesService.js: how a number looks is
// a UI decision, and the service should only know about the wire.
// ─────────────────────────────────────────────────────────────────────────────

export const DASH = "—";

/** True only for values the backend didn't report. 0 and false are readings. */
export const isMissing = (v) => v === null || v === undefined || v === "";

/** Any value, dashed when missing. */
export const dash = (v) => (isMissing(v) ? DASH : v);

/** Integer with thousands separators: 4189 → "4,189". */
export const fmtCount = (n) =>
  (Number.isFinite(n) ? n.toLocaleString("en-US") : DASH);

/** Percentage: 34 → "34%". `digits` for fractional precision. */
export const fmtPercent = (n, digits = 0) =>
  (Number.isFinite(n) ? `${n.toFixed(digits)}%` : DASH);

/** Round-trip latency: 24 → "24ms". */
export const fmtLatency = (ms) =>
  (Number.isFinite(ms) ? `${Math.round(ms)}ms` : DASH);

/** Duration from seconds: 1209600 → "14d 0h", 7200 → "2h". */
export function fmtUptime(seconds) {
  if (!Number.isFinite(seconds)) return DASH;
  const days = Math.floor(seconds / 86_400);
  const hrs  = Math.floor((seconds % 86_400) / 3600);
  return days > 0 ? `${days}d ${hrs}h` : `${hrs}h`;
}

/** Throughput: 2.4e9 → "2.40 Gbps". */
export const fmtBandwidth = (bps) =>
  (Number.isFinite(bps) ? `${(bps / 1_000_000_000).toFixed(2)} Gbps` : DASH);

/**
 * The UPTIME column. The design shows a percentage, and /enterprise-nodes serves
 * uptime_percent off the authority row, so that is normally what renders. The
 * duration fallback stays for rows where it is null — it used to be the usual
 * case, because the old /nodes surface dropped the field in enrich().
 */
export const fmtUptimeCell = (node) =>
  (Number.isFinite(node.uptime_percent)
    ? fmtPercent(node.uptime_percent, 2)
    : fmtUptime(node.uptime_seconds));

/**
 * How to name a node on screen.
 *
 * The designs label nodes by their short name ("N-EU-W1"), and
 * /enterprise-nodes returns `name`, so that is normally what shows. The node_id
 * fallback remains for rows without one — a real identifier for the same node,
 * which beats a dash in a heading or a type-to-confirm prompt. It was the usual
 * outcome on the old /nodes surface, which never sent the field.
 *
 * Returns "" when there is nothing at all, so callers needing a comparable token
 * can test for empty and callers that display it can wrap in dash().
 */
export const nodeLabel = (node) => node?.name || node?.node_id || "";
